import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import InputBox from '../../components/InputBox';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import Modal from '../../components/Modal';
import GoogleSignIn from '../../components/GoogleSignIn';
import { useSettings } from '../../contexts/SettingsContext';

const SignupPage = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    mobileNumber: '',
    agreeTerms: false,
    role: 'HR',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '' });
  
  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';

  // Helper to close modal
  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  // Helper to show error modal
  const showErrorModal = (title, message) => {
    setModal({
      isOpen: true,
      type: 'error',
      title,
      message,
    });
  };

  const fieldForSignupError = (message = '') => {
    const normalized = message.toLowerCase();
    if (normalized.includes('name')) return 'fullName';
    if (normalized.includes('email')) return 'email';
    if (normalized.includes('phone') || normalized.includes('mobile')) return 'mobileNumber';
    if (normalized.includes('confirm')) return 'confirmPassword';
    if (normalized.includes('password')) return 'password';
    return null;
  };

  const setSignupError = (message) => {
    const field = fieldForSignupError(message);
    setErrors(field ? { [field]: message } : {});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    // Special handling for specific fields
    if (name === 'fullName') {
      processedValue = value.replace(/[^a-zA-Z.\s]/g, '');
    } else if (name === 'mobileNumber') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue,
    }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Calculate password strength
    if (name === 'password') {
      calculatePasswordStrength(processedValue);
    }
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length === 0) {
      setPasswordStrength({ score: 0, label: '' });
      return;
    }
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[\W_]/.test(password)) score++;

    const labels = ['', 'Very Weak', 'Weak', 'Good', 'Strong'];
    setPasswordStrength({ score, label: labels[score] || '' });
  };

  const getStrengthColor = (index) => {
    if (passwordStrength.score === 0) return 'bg-gray-200 dark:bg-gray-700';
    if (index < passwordStrength.score) {
      if (passwordStrength.score <= 1) return 'bg-red-500';
      if (passwordStrength.score === 2) return 'bg-yellow-500';
      return 'bg-green-500';
    }
    return 'bg-gray-200 dark:bg-gray-700';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (formData.mobileNumber.length < 10) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const SECRET_KEY = process.env.REACT_APP_SECRETKEY;

      if (!SECRET_KEY) {
        showErrorModal(
          'Configuration Error',
          'Missing REACT_APP_SECRETKEY in ux/.env (used for encrypting signup payload).'
        );
        return;
      }

      // Role mapping: always HR (role_id = 1) since job seekers use separate portal
      const roleId = 1;
      
      const payload = {
        email: formData.email,
        name: formData.fullName,
        phone_no: formData.mobileNumber,
        password: formData.password,
        role_id: roleId,
      };

      // Encrypt payload using CryptoJS AES (same as login)
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: encrypted,
        }),
      });

      const data = await response.json();

      if (response.ok && (data.message?.includes('successful') || data.message === 'Success')) {
        // Navigate to login page with registered=true param
        // The login page will show the success modal
        navigate('/auth/login?registered=true');
      } else {
        // Show error modal
        showErrorModal(
          'Registration Failed',
          data.error || 'Registration failed. Please try again.'
        );
        setSignupError(data.error || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration failed:', error);
      showErrorModal(
        'Connection Error',
        'Please make sure the backend server is running.'
      );
      setErrors({});
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 flex items-stretch transition-[background-color] duration-250 ease-out">
      {/* Left Brand Panel (desktop) - Auth.svg Image */}
      <div className="hidden lg:flex lg:w-[45%] transition-colors duration-250">
        {/* Outer padding wrapper */}
        <div className="w-full h-full py-4 pl-4 pr-2">
          {/* Rounded image container - full size */}
          <div className="relative w-full h-full rounded-3xl overflow-hidden">
            <img
              src="/assets/auth/Auth.svg"
              alt="Welcome illustration"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Right Content - Signup Form - ~55% width */}
      <div className="flex-1 flex flex-col lg:w-[55%] min-w-0 transition-colors duration-250 h-full overflow-y-auto">

        {/* Signup Content - center aligned with larger scale */}
        <main
          className="flex-1 flex items-center justify-center"
          style={{
            paddingLeft: 'clamp(8px, 4vw, 64px)',
            paddingRight: 'clamp(8px, 4vw, 64px)',
            paddingTop: 'clamp(8px, 3vw, 32px)',
            paddingBottom: 'clamp(8px, 3vw, 32px)'
          }}
        >
          <div className="w-full max-w-2xl">
            <div>
              <div className="mb-4 mt-1">
                <h1 className="text-5xl font-medium tracking-normal transition-colors duration-250 text-gray-700 dark:text-gray-200">
                  Create your profile
                </h1>
                <p className="mt-1.5 text-base font-normal transition-colors duration-250 text-gray-500 dark:text-gray-400">
                  Join {settings.app.name} and start hiring smarter.
                </p>
              </div>

              {/* Role set by URL param from landing page */}

              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Full Name - Full width */}
                <InputBox
                  label="Full Name"
                  type="text"
                  name="fullName"
                  placeholder="What is your name?"
                  value={formData.fullName}
                  onChange={handleChange}
                  error={errors.fullName}
                  required
                  autoComplete="name"
                />

                {/* Email and Mobile Number - Side by side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputBox
                    label="Email ID"
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                    autoComplete="email"
                  />

                  <InputBox
                    label="Mobile Number"
                    type="tel"
                    name="mobileNumber"
                    placeholder="10-digit number"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    error={errors.mobileNumber}
                    required
                    autoComplete="tel"
                  />
                </div>

                {/* Password and Confirm Password - Side by side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <InputBox
                      label="Password"
                      type="password"
                      name="password"
                      placeholder="Min 6 characters"
                      value={formData.password}
                      onChange={handleChange}
                      error={errors.password}
                      required
                      autoComplete="new-password"
                    />
                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <div className="mt-1.5">
                        <div className="flex gap-1">
                          {[0, 1, 2, 3].map((index) => (
                            <div
                              key={index}
                              className={`h-1 flex-1 rounded-full transition-colors duration-250 ${getStrengthColor(index)}`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-right mt-0.5 text-gray-500 dark:text-gray-400 transition-colors duration-250">
                          {passwordStrength.label || 'Password Strength'}
                        </p>
                      </div>
                    )}
                  </div>

                  <InputBox
                    label="Confirm Password"
                    type="password"
                    name="confirmPassword"
                    placeholder="Re-enter password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={errors.confirmPassword}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="pt-0.5">
                  <div>
                    <Checkbox
                      label={
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          I agree to the{' '}
                          <Link
                            to="/terms"
                            className="font-medium hover:underline"
                            style={{ color: primaryColor }}
                          >
                            Terms and Conditions
                          </Link>{' '}
                          &{' '}
                          <Link
                            to="/privacy"
                            className="font-medium hover:underline"
                            style={{ color: primaryColor }}
                          >
                            Privacy Policy
                          </Link>
                        </span>
                      }
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleChange}
                    />
                    {errors.agreeTerms && (
                      <p className="text-red-500 text-xs mt-1">{errors.agreeTerms}</p>
                    )}
                  </div>
                </div>

                <div className="pt-0.5">
                  <Button
                    type="submit"
                    size="lg"
                    loading={isLoading}
                    disabled={!formData.fullName || !formData.email || !formData.mobileNumber || !formData.password || !formData.confirmPassword || !formData.agreeTerms}
                    fullWidth
                    className="rounded-xl"
                  >
                    Register
                  </Button>
                </div>
              </form>

              {/* OR Divider */}
              <div className="mt-3.5 flex items-center gap-4">
                <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">Or</span>
                <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
              </div>

              {/* Google Sign In */}
              <div className="mt-3">
                <GoogleSignIn
                  onError={(message) => setErrors({ email: message })}
                  setIsLoading={setIsLoading}
                  isSignup={true}
                  candidate={false}
                />
              </div>

              <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400 transition-colors duration-250">
                Already have an account?{' '}
                <Link
                  to="/auth/login"
                  className="font-semibold hover:underline"
                  style={{ color: primaryColor }}
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </main>
      </div>

      {/* Modal for success/error messages */}
      <Modal
        isOpen={modal.isOpen}
        onClose={() => {
          closeModal();
          if (modal.onConfirm) modal.onConfirm();
        }}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText={modal.type === 'success' ? 'Continue' : 'Got it'}
      />
    </div>
  );
};

export default SignupPage;
